import { useState, memo } from 'react'
import { formatRelativeTime } from '../../utils/mockData'
import ContactAvatar from '../shared/ContactAvatar'

/**
 * Determina el color del indicador de tiempo basado en la antigüedad
 */
function getTimeIndicatorColor(timestamp) {
  if (!timestamp) return 'bg-gray-300'

  const now = new Date()
  const msgTime = new Date(timestamp)
  const diffHours = (now - msgTime) / (1000 * 60 * 60)

  if (diffHours < 1) return 'bg-green-500'      // Menos de 1 hora - verde
  if (diffHours < 24) return 'bg-emerald-400'   // Hoy - verde claro
  if (diffHours < 48) return 'bg-yellow-400'    // Ayer - amarillo
  if (diffHours < 168) return 'bg-orange-400'   // Esta semana - naranja
  return 'bg-gray-400'                          // Más antiguo - gris
}

/**
 * LabelBadges - Muestra etiquetas como badges pill con tooltip para overflow
 */
const LabelBadges = memo(function LabelBadges({ labels = [] }) {
  const [showTooltip, setShowTooltip] = useState(false)
  const validLabels = labels.filter(l => l && l.id)

  if (validLabels.length === 0) return null

  // Mostrar máximo 2 etiquetas visibles + contador
  const visibleLabels = validLabels.slice(0, 2)
  const hiddenCount = validLabels.length - 2

  return (
    <div
      className="relative flex flex-wrap items-center gap-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {visibleLabels.map((label, idx) => (
        <span
          key={label.id || idx}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium truncate max-w-[100px]"
          style={{
            backgroundColor: `${label.color}20` || '#6B728020',
            color: label.color || '#6B7280',
            border: `1px solid ${label.color}40` || '#6B728040'
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: label.color || '#6B7280' }}
          />
          <span className="truncate">{label.name}</span>
        </span>
      ))}

      {hiddenCount > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          +{hiddenCount}
        </span>
      )}

      {/* Tooltip con todas las etiquetas - siempre visible al hover */}
      {showTooltip && (
        <div className="absolute bottom-full left-0 mb-1 z-50 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg max-w-[220px]">
          <div className="font-medium mb-1.5 text-gray-300">Etiquetas ({validLabels.length})</div>
          {validLabels.map((label, idx) => (
            <div key={label.id || idx} className="flex items-center gap-1.5 py-0.5">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: label.color || '#6B7280' }}
              />
              <span className="break-words">{label.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

/**
 * CampaignBadge - Muestra campañas completadas con tooltip
 */
const CampaignBadge = memo(function CampaignBadge({ campaigns = [] }) {
  const [showTooltip, setShowTooltip] = useState(false)
  const validCampaigns = campaigns.filter(c => c && c.campaign_id)

  if (validCampaigns.length === 0) return null

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    try {
      return new Date(dateStr).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
    } catch {
      return ''
    }
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
        <span className="text-green-500">✓</span>
        <span>{validCampaigns.length} campaña{validCampaigns.length > 1 ? 's' : ''}</span>
      </span>

      {showTooltip && (
        <div className="absolute bottom-full right-0 mb-1 z-50 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg min-w-44">
          <div className="font-medium mb-1.5 text-green-400">Campañas Completadas</div>
          {validCampaigns.map((campaign, idx) => (
            <div key={campaign.id || idx} className="py-1 border-t border-gray-700 first:border-0">
              <div className="flex items-center gap-1">
                <span className="text-green-400">✓</span>
                <span className="font-medium">{campaign.campaign_name}</span>
              </div>
              <div className="text-gray-400 text-[10px] ml-4">
                {campaign.steps_completed}/{campaign.total_steps} pasos • {formatDate(campaign.completed_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

export default memo(function ChatItem({ chat, isSelected, onClick }) {
  const timeColor = getTimeIndicatorColor(chat.lastMessageTime)

  return (
    <div
      onClick={onClick}
      className={`
        bg-white rounded-xl p-4 cursor-pointer transition-all duration-200
        ${isSelected
          ? 'shadow-md ring-2 ring-primary-500 ring-opacity-50'
          : 'shadow-sm hover:shadow-md border border-gray-100 hover:border-gray-200'
        }
      `}
    >
      <div className="flex items-start gap-3">
        {/* Avatar con degradado local - sin solicitudes externas */}
        <div className="flex-shrink-0 relative">
          <ContactAvatar name={chat.name} phone={chat.phone} seed={chat.avatar_seed}
            className="w-12 h-12 rounded-full text-sm shadow-md" />
          {/* Indicador de mensajes no leídos en avatar */}
          {chat.unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full shadow">
              {chat.unreadCount > 9 ? '9+' : chat.unreadCount}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header: Name + Time indicator */}
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {chat.name}
            </h3>
            <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
              <span className={`w-2 h-2 rounded-full ${timeColor}`}></span>
              <span className="text-xs text-gray-500">
                {formatRelativeTime(chat.lastMessageTime)}
              </span>
            </div>
          </div>

          {/* Last message - estilo Koomo con color primario */}
          <p className="text-sm text-primary-600 truncate mb-2">
            {chat.lastMessage || 'Sin mensajes'}
          </p>

          {/* Iconos del prospecto */}
          {chat.icons && chat.icons.length > 0 && (
            <div className="flex gap-1 mb-2">
              {chat.icons.map((icon, index) => (
                <span
                  key={index}
                  className="text-base bg-gray-50 rounded px-1"
                  title={icon.nombre}
                >
                  {icon.emoji}
                </span>
              ))}
            </div>
          )}

          {/* Labels y Campaigns como badges */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <LabelBadges labels={chat.labels} />
            <CampaignBadge campaigns={chat.completedCampaigns} />
          </div>

          {/* Footer: Agent + Department como badges pill */}
          <div className="flex items-center gap-2">
            {chat.assignedTo === 'Ninguno' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                Sin asignar
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                <span className="text-gray-400">👤</span>
                {chat.assignedTo}
              </span>
            )}

            {chat.department && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                <span className="text-purple-400">🏢</span>
                {chat.department}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})
